import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { SHIPPING_NOTICE, SHIPPING_METHOD, SHIPPING_TIME, FREE_SHIPPING_THRESHOLD, CONTACT_INFO, siteImages } from '../data/mock';
import { Truck, Clock, MapPin, Package, Shield, Glasses } from 'lucide-react';

const ShippingPage = () => {
  useEffect(() => {
    document.title = 'Shipping Information | Focus Your Style';
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <CartDrawer />
      
      <main className="pt-[105px]">
        {/* Hero Banner */}
        <section className="relative h-[200px] overflow-hidden">
          <img 
            src={siteImages.shopBanner}
            alt="Shipping Information"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/90 via-neutral-900/70 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-xs font-medium mb-4">
                  <Truck className="w-3.5 h-3.5" />
                  Fast Delivery
                </span>
                <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold">
                  Shipping Information
                </h1>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Free Shipping Banner */}
          <div className="bg-gradient-to-r from-focus-600 to-focus-700 rounded-2xl p-8 mb-12 text-white text-center">
            <Truck className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-2xl font-display font-semibold mb-2">Free Shipping on Orders ${FREE_SHIPPING_THRESHOLD}+</h2>
            <p className="text-focus-200">{SHIPPING_NOTICE}</p>
          </div>

          {/* Shipping Details */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white border border-neutral-200 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-focus-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Truck className="w-6 h-6 text-focus-600" />
              </div>
              <h3 className="font-semibold text-neutral-900 mb-2">Shipping Method</h3>
              <p className="text-neutral-600">{SHIPPING_METHOD}</p>
            </div>
            <div className="bg-white border border-neutral-200 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-focus-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-6 h-6 text-focus-600" />
              </div>
              <h3 className="font-semibold text-neutral-900 mb-2">Delivery Time</h3>
              <p className="text-neutral-600">{SHIPPING_TIME}</p>
            </div>
            <div className="bg-white border border-neutral-200 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-focus-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-6 h-6 text-focus-600" />
              </div>
              <h3 className="font-semibold text-neutral-900 mb-2">Shipping Area</h3>
              <p className="text-neutral-600">United States Only</p>
            </div>
          </div>

          {/* Shipping Policy */}
          <div className="bg-white border border-neutral-200 rounded-2xl p-8">
            <h2 className="font-display text-xl text-neutral-900 font-semibold mb-6">Shipping Policy</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">Processing Time</h3>
                <p className="text-neutral-600">Orders are typically processed within 1-2 business days. You will receive a confirmation email with tracking information once your order ships.</p>
              </div>
              
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">Delivery Estimates</h3>
                <p className="text-neutral-600">Standard delivery takes {SHIPPING_TIME} after your order ships. Delivery times may vary based on your location and carrier conditions.</p>
              </div>
              
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">Tracking Your Order</h3>
                <p className="text-neutral-600">Once your order ships, you'll receive an email with tracking information. You can track your package directly through the carrier's website.</p>
              </div>
              
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">Shipping Restrictions</h3>
                <p className="text-neutral-600">We currently ship only within the United States. We cannot ship to PO Boxes or APO/FPO addresses at this time.</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="mt-12 bg-neutral-100 rounded-2xl p-8 text-center">
            <h3 className="font-semibold text-neutral-900 mb-2">Questions About Shipping?</h3>
            <p className="text-neutral-600 mb-4">Contact our customer support team</p>
            <a 
              href={`mailto:${CONTACT_INFO.email}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-neutral-900 hover:bg-focus-600 text-white font-medium rounded-xl transition-colors"
            >
              {CONTACT_INFO.email}
            </a>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ShippingPage;
